import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarCheck, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { trackEvent, EVENTS } from "@/lib/analytics";

const SCROLL_THRESHOLD = 150;

const DISMISS_KEY = "gpv:mobile-book-cta-dismissed-at";
const SUPPRESS_HOURS = 24;

const SHOW_ON = ["/", "/find-vets", "/vet-directory", "/compare-prices", "/getvideovet", "/emergency-vet"];

/**
 * Sticky "Book a vet now" call-to-action surfaced on mobile only.
 * Sits just above MobileBottomNav (h ~ 56px + safe-area).
 */
const MobileBookCTA = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash
  const [scrolled, setScrolled] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return setDismissed(false);
      const at = Number(raw);
      if (!Number.isFinite(at)) return setDismissed(false);
      setDismissed(Date.now() - at < SUPPRESS_HOURS * 3600 * 1000);
    } catch {
      setDismissed(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const allowed =
    SHOW_ON.includes(location.pathname) || location.pathname.startsWith("/vets-in/");
  if (!isMobile || !allowed) return null;

  const visible = scrolled && !dismissed;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className={cn(
        "fixed inset-x-0 z-40 md:hidden px-3",
        // Sit above the bottom nav (approx 56px) + iOS safe area.
        "bottom-[calc(56px+env(safe-area-inset-bottom))]",
        visible
          ? prefersReducedMotion
            ? "opacity-100 translate-y-0"
            : "animate-slide-in-bottom"
          : prefersReducedMotion
            ? "opacity-0 pointer-events-none translate-y-0"
            : "animate-slide-out-bottom pointer-events-none",
      )}
      role="region"
      aria-label="Quick booking"
    >
      <div className="mx-auto max-w-md rounded-full shadow-lg bg-primary text-primary-foreground flex items-center pl-4 pr-1 py-1 gap-2">
        <CalendarCheck className="w-4 h-4 shrink-0" aria-hidden />
        <Link
          to="/vet-directory"
          onClick={() => trackEvent(EVENTS.BOOK_APPOINTMENT_START, { source: "mobile_sticky_cta" })}
          className="flex-1 text-sm font-semibold py-2"
        >
          Book a vet now
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-primary-foreground/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default MobileBookCTA;
